import { useEffect, useState } from 'react'
import {
  locale,
  setLocale,
  offChangeLocale,
  onChangeLocale,
} from '@signavio/i18n'

import { useApi } from '../../api'
import {
  useUser,
  useCallbackWhenRequestFulfills,
  useCallbackWhenRequestFails,
} from '../../hooks'
import types from '../../apiTypes'
import { Nullable } from '@signavio/effektif-commons'

type LocaleProviderProps = React.FC<React.PropsWithChildren<unknown>>

const LocaleProvider: LocaleProviderProps = ({ children }) => {
  const user = useUser()
  const [languageLoaded, setLanguageLoaded] = useState(false)
  const [currentLanguge, setCurrentLanguage] = useState<Nullable<string>>(null)

  const [fetchPreferences] = useApi(types.USER_PREFERENCE, {
    query: {
      id: user.id,
    },
  })

  const [, updatePreferences] = useApi(types.USER_PREFERENCE, {
    method: 'update',
    query: {
      id: user.id,
    },
  })

  useCallbackWhenRequestFulfills(fetchPreferences, () => {
    const preferences = fetchPreferences.value

    if (preferences.language) {
      void setLocale(preferences.language)
      setCurrentLanguage(preferences.language)
    } else {
      // ensure user settings reflect language
      updatePreferences({ language: locale() })
      void setLocale(locale())
      setCurrentLanguage(locale())
    }
  })

  useCallbackWhenRequestFails(fetchPreferences, () => {
    // render the content with the default locale if loading the user language failed
    void setLocale(locale())
    setCurrentLanguage(locale())
  })

  useEffect(() => {
    if (currentLanguge) {
      document.documentElement.setAttribute(
        'lang',
        currentLanguge.replace('_', '-')
      )
    }
  }, [currentLanguge])

  useEffect(() => {
    const callback = () => setLanguageLoaded(true)
    onChangeLocale(callback)
    return () => {
      offChangeLocale(callback)
    }
  }, [setLanguageLoaded])

  if (!languageLoaded) {
    return null
  }

  return <>{children}</>
}

export default LocaleProvider
